/**
 * API Status Context
 *
 * Detects when the backend is unavailable (e.g., ECS scaled to zero in cloud)
 * and shows a user-friendly overlay with retry functionality.
 *
 * Key design: REACTIVE, not proactive. Only checks health when an API call fails
 * with a 503 status, to avoid waking up a hibernating backend unnecessarily.
 *
 * Note: This feature is disabled on localhost since scale-to-zero only applies
 * to cloud deployments. Local network errors are typically due to the backend
 * not running, which developers can handle themselves.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { CloudOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { API_BASE_URL } from '../api/config';
import { BACKEND_UNAVAILABLE_EVENT, REQUEST_SUCCESS_EVENT, API_HTML_RESPONSE_EVENT } from '../api/axiosInstance';
import { isLocalDev } from '../utils/platform';
import { getGenericErrorMessage } from '../utils/errors';

interface ApiStatusContextType {
  isBackendUnavailable: boolean;
  reportBackendUnavailable: () => void;
  reportSuccessfulRequest: () => void;
}

const ApiStatusContext = createContext<ApiStatusContextType | undefined>(undefined);

const HEALTH_CHECK_INTERVAL_MS = 3000;
const MAX_RETRY_INTERVAL_MS = 30000;

export function ApiStatusProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  const [isBackendUnavailable, setIsBackendUnavailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const consecutiveFailuresRef = useRef(0);

  const stopHealthCheck = useCallback(() => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }
  }, []);

  const checkHealth = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        cache: 'no-store',
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  const startHealthCheck = useCallback(() => {
    if (healthCheckIntervalRef.current) return;

    const doCheck = async () => {
      setIsChecking(true);
      const isHealthy = await checkHealth();
      setIsChecking(false);

      if (isHealthy) {
        setIsBackendUnavailable(false);
        setRetryCount(0);
        consecutiveFailuresRef.current = 0;
        stopHealthCheck();
      } else {
        setRetryCount((c) => c + 1);
      }
    };

    // Check immediately
    doCheck();

    // Then check periodically with exponential backoff capped at MAX_RETRY_INTERVAL_MS
    const interval = Math.min(
      HEALTH_CHECK_INTERVAL_MS * Math.pow(1.5, retryCount),
      MAX_RETRY_INTERVAL_MS
    );
    healthCheckIntervalRef.current = setInterval(doCheck, interval);
  }, [checkHealth, stopHealthCheck, retryCount]);

  // Report backend unavailable (503 from ALB when ECS scaled to zero)
  const reportBackendUnavailable = useCallback(() => {
    // Skip on localhost - scale-to-zero only applies to cloud deployments
    if (isLocalDev) return;

    consecutiveFailuresRef.current += 1;
    // Only show overlay after 2 consecutive failures to avoid false positives
    if (consecutiveFailuresRef.current >= 2) {
      setIsBackendUnavailable(true);
      startHealthCheck();
    }
  }, [startHealthCheck]);

  // Report a successful request - resets failure counter
  const reportSuccessfulRequest = useCallback(() => {
    consecutiveFailuresRef.current = 0;
    if (isBackendUnavailable) {
      setIsBackendUnavailable(false);
      setRetryCount(0);
      stopHealthCheck();
    }
  }, [isBackendUnavailable, stopHealthCheck]);

  // Listen for axios events
  useEffect(() => {
    const handleBackendUnavailable = () => reportBackendUnavailable();
    const handleRequestSuccess = () => reportSuccessfulRequest();
    const handleHtmlResponse = () => {
      // Show a toast notification for HTML response errors
      // This happens when CDN/proxy misconfiguration returns HTML instead of JSON
      toast.error(
        t('errors.htmlResponse', 'A technical error occurred. Please try again or refresh the page.'),
        {
          duration: 6000,
          description: getGenericErrorMessage(),
        }
      );
    };

    window.addEventListener(BACKEND_UNAVAILABLE_EVENT, handleBackendUnavailable);
    window.addEventListener(REQUEST_SUCCESS_EVENT, handleRequestSuccess);
    window.addEventListener(API_HTML_RESPONSE_EVENT, handleHtmlResponse);

    return () => {
      window.removeEventListener(BACKEND_UNAVAILABLE_EVENT, handleBackendUnavailable);
      window.removeEventListener(REQUEST_SUCCESS_EVENT, handleRequestSuccess);
      window.removeEventListener(API_HTML_RESPONSE_EVENT, handleHtmlResponse);
      stopHealthCheck();
    };
  }, [reportBackendUnavailable, reportSuccessfulRequest, stopHealthCheck, t]);

  const handleManualRetry = useCallback(() => {
    setRetryCount(0);
    startHealthCheck();
  }, [startHealthCheck]);

  return (
    <ApiStatusContext.Provider
      value={{
        isBackendUnavailable,
        reportBackendUnavailable,
        reportSuccessfulRequest,
      }}
    >
      {children}
      {isBackendUnavailable && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/90">
          <Card className="max-w-[400px] text-center">
            <CardContent className="p-8">
              <CloudOff className="mx-auto mb-4 h-16 w-16 text-warning" />
              <h2 className="mb-2 text-xl font-semibold">
                {t('backend.unavailable.title', 'System Starting Up')}
              </h2>
              <p className="mb-6 text-muted-foreground">
                {t(
                  'backend.unavailable.message',
                  'The system is waking up from hibernation. This usually takes about 60 seconds.'
                )}
              </p>
              {isChecking ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    {t('backend.unavailable.checking', 'Checking connection...')}
                  </span>
                </div>
              ) : (
                <Button onClick={handleManualRetry} className="mt-2">
                  {t('app.retry', 'Retry')}
                </Button>
              )}
              {retryCount > 0 && (
                <p className="mt-4 text-xs text-muted-foreground">
                  {t('backend.unavailable.retryCount', 'Retry attempt: {{count}}', { count: retryCount })}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </ApiStatusContext.Provider>
  );
}

export function useApiStatus(): ApiStatusContextType {
  const context = useContext(ApiStatusContext);
  if (context === undefined) {
    throw new Error('useApiStatus must be used within an ApiStatusProvider');
  }
  return context;
}

/**
 * Check if an error indicates the backend is unavailable due to scale-to-zero.
 * This specifically checks for 503 responses (ALB returns 503 when no healthy targets).
 */
export function isBackendUnavailableError(error: unknown): boolean {
  if (!error) return false;

  // Check for 503 response (ALB returns this when ECS is scaled to zero)
  if (typeof error === 'object' && error !== null) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 503) {
      return true;
    }
  }

  return false;
}
